import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import ExerciseScheduleForm from "./ExerciseScheduleForm";
import { fetchExerciseSchedule, updateExerciseSchedule } from "../../store/slices/exerciseSchedulesSlice";

const EditExerciseSchedule = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentExerciseSchedule, isLoading, error } = useSelector(
        (state) => state.exerciseSchedules
    );

    const [formData, setFormData] = useState({
        caseId: caseId || null,
        patientId: patientId || null,
        weight: "",
        bmi: "",
        goal: "",
        plan: "",
        clientName: "",
        responsibleStaff: "",
        scheduleRows: [],
        effectiveDate: "",
        approvedBy: "",
        approvedDate: "",
        nextRevisionBy: "",
        nextRevisionDate: ""
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Helper function to map snake_case backend data to camelCase frontend format
    const mapBackendToFrontend = (backendData) => {
        if (!backendData) return null;

        // Map rows from backend to frontend format
        const scheduleRows = backendData.rows?.map(row => {
            // Map physical activities from backend fields
            const physicalActivities = {};
            const gymExercises = {};

            // Map physical activities
            if (row.phys_stretching) {
                physicalActivities["Stretching Exercise"] = {
                    done: row.phys_stretching,
                    duration: row.phys_stretching_duration || "",
                    reason: row.phys_stretching_reason || ""
                };
            }

            if (row.phys_walking) {
                physicalActivities["Walking"] = {
                    done: row.phys_walking,
                    duration: row.phys_walking_duration || "",
                    reason: row.phys_walking_reason || ""
                };
            }

            if (row.phys_strengthening) {
                physicalActivities["Strengthening Exercise"] = {
                    done: row.phys_strengthening,
                    duration: row.phys_strengthening_duration || "",
                    reason: row.phys_strengthening_reason || ""
                };
            }

            // Map gym exercises
            if (row.gym_tread) {
                gymExercises["Tread Mill"] = {
                    done: row.gym_tread,
                    duration: row.gym_tread_duration || "",
                    reason: row.gym_tread_reason || ""
                };
            }

            if (row.gym_bicycle) {
                gymExercises["Bi-Cycle"] = {
                    done: row.gym_bicycle,
                    duration: row.gym_bicycle_duration || "",
                    reason: row.gym_bicycle_reason || ""
                };
            }

            if (row.gym_anysport) {
                gymExercises["Any Sport Activity"] = {
                    done: row.gym_anysport,
                    duration: row.gym_anysport_duration || "",
                    reason: row.gym_anysport_reason || ""
                };
            }

            return {
                date: row.date ? row.date.split('T')[0] : "",
                time: row.time || "",
                weeklyOutcome: row.weekly_outcome || "",
                staffName: row.staff_name || "",
                physicalActivities,
                gymExercises
            };
        }) || [];

        return {
            caseId: backendData.case_id || null,
            patientId: backendData.patient_id || null,
            weight: backendData.weight || "",
            bmi: backendData.bmi || "",
            goal: backendData.goal || "",
            plan: backendData.plan || "",
            clientName: backendData.client_name || "",
            responsibleStaff: backendData.responsible_staff || "",
            scheduleRows,
            effectiveDate: backendData.effective_date ? backendData.effective_date.split('T')[0] : "",
            approvedBy: backendData.approved_by || "",
            approvedDate: backendData.approved_date ? backendData.approved_date.split('T')[0] : "",
            nextRevisionBy: backendData.next_revision_by || "",
            nextRevisionDate: backendData.next_revision_date ? backendData.next_revision_date.split('T')[0] : ""
        };
    };

    // Fetch data on component mount
    useEffect(() => {
        if (id) {
            dispatch(fetchExerciseSchedule(id));
        }
    }, [id, dispatch]);

    // Update form data when backend data is loaded
    useEffect(() => {
        if (currentExerciseSchedule) {
            const frontendData = mapBackendToFrontend(currentExerciseSchedule);
            if (frontendData) {
                setFormData(frontendData);
            }
        }
    }, [currentExerciseSchedule]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addRow = () => {
        setFormData(prev => ({
            ...prev,
            scheduleRows: [
                ...prev.scheduleRows,
                {
                    date: "",
                    time: "",
                    weeklyOutcome: "",
                    staffName: "",
                    physicalActivities: {},
                    gymExercises: {}
                }
            ]
        }));
    };

    const removeRow = (index) => {
        setFormData(prev => ({
            ...prev,
            scheduleRows: prev.scheduleRows.filter((_, i) => i !== index)
        }));
    };

    // Prepare the payload for API (same as Add component)
    const preparePayload = () => {
        // Convert scheduleRows to the format expected by backend
        const rows = formData.scheduleRows.map(row => {
            // Map physicalActivities to backend fields
            const physicalActivities = row.physicalActivities || {};

            return {
                date: row.date || null,
                time: row.time || null,
                weekly_outcome: row.weeklyOutcome || null,
                staff_name: row.staffName || null,

                // Physical activities mapping
                phys_stretching: physicalActivities["Stretching Exercise"]?.done || null,
                phys_stretching_duration: physicalActivities["Stretching Exercise"]?.done === "yes"
                    ? physicalActivities["Stretching Exercise"]?.duration || null
                    : null,
                phys_stretching_reason: physicalActivities["Stretching Exercise"]?.done === "no"
                    ? physicalActivities["Stretching Exercise"]?.reason || null
                    : null,

                phys_walking: physicalActivities["Walking"]?.done || null,
                phys_walking_duration: physicalActivities["Walking"]?.done === "yes"
                    ? physicalActivities["Walking"]?.duration || null
                    : null,
                phys_walking_reason: physicalActivities["Walking"]?.done === "no"
                    ? physicalActivities["Walking"]?.reason || null
                    : null,

                phys_strengthening: physicalActivities["Strengthening Exercise"]?.done || null,
                phys_strengthening_duration: physicalActivities["Strengthening Exercise"]?.done === "yes"
                    ? physicalActivities["Strengthening Exercise"]?.duration || null
                    : null,
                phys_strengthening_reason: physicalActivities["Strengthening Exercise"]?.done === "no"
                    ? physicalActivities["Strengthening Exercise"]?.reason || null
                    : null,

                // Gym exercises mapping
                gym_tread: row.gymExercises?.["Tread Mill"]?.done || null,
                gym_tread_duration: row.gymExercises?.["Tread Mill"]?.done === "yes"
                    ? row.gymExercises["Tread Mill"]?.duration || null
                    : null,
                gym_tread_reason: row.gymExercises?.["Tread Mill"]?.done === "no"
                    ? row.gymExercises["Tread Mill"]?.reason || null
                    : null,

                gym_bicycle: row.gymExercises?.["Bi-Cycle"]?.done || null,
                gym_bicycle_duration: row.gymExercises?.["Bi-Cycle"]?.done === "yes"
                    ? row.gymExercises["Bi-Cycle"]?.duration || null
                    : null,
                gym_bicycle_reason: row.gymExercises?.["Bi-Cycle"]?.done === "no"
                    ? row.gymExercises["Bi-Cycle"]?.reason || null
                    : null,

                gym_anysport: row.gymExercises?.["Any Sport Activity"]?.done || null,
                gym_anysport_duration: row.gymExercises?.["Any Sport Activity"]?.done === "yes"
                    ? row.gymExercises["Any Sport Activity"]?.duration || null
                    : null,
                gym_anysport_reason: row.gymExercises?.["Any Sport Activity"]?.done === "no"
                    ? row.gymExercises["Any Sport Activity"]?.reason || null
                    : null
            };
        });

        // Main payload
        const payload = {
            case_id: formData.caseId,
            patient_id: formData.patientId,
            weight: formData.weight || null,
            bmi: formData.bmi || null,
            goal: formData.goal || null,
            plan: formData.plan || null,
            client_name: formData.clientName || null,
            responsible_staff: formData.responsibleStaff || null,
            document_name: "Weekly Clients Exercise Schedule",
            effective_date: formData.effectiveDate || null,
            approved_by: formData.approvedBy || null,
            approved_date: formData.approvedDate || null,
            next_revision_by: formData.nextRevisionBy || null,
            next_revision_date: formData.nextRevisionDate || null,
            rows: rows
        };

        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.clientName || !formData.responsibleStaff) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in Client Name and Responsible Staff",
                icon: "warning",
            });
            return;
        }

        if (formData.scheduleRows.length === 0) {
            Swal.fire({
                title: "Missing Data",
                text: "Please add at least one schedule row",
                icon: "warning",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = preparePayload();

            await dispatch(updateExerciseSchedule({
                case_id: caseId,
                scheduleData: payload
            })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Exercise Schedule updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            // Stay on edit page
        } catch (error) {
            console.error("Error updating exercise schedule:", error);

            let errorMessage = "Failed to update exercise schedule";
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error?.errors) {
                // Handle validation errors
                const errorMessages = Object.values(error.errors)
                    .flat()
                    .join("\n");
                errorMessage = errorMessages;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                title: "Error!",
                text: errorMessage,
                icon: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Show loading state
    if (isLoading && !formData.clientName) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <Breadcrumbs />
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading exercise schedule data...</p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                {/* Show error if any */}
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                        {error}
                        <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <ExerciseScheduleForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                                addRow={addRow}
                                removeRow={removeRow}
                                isSubmitting={isSubmitting}
                            />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isSubmitting || isLoading}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-edit-2-line me-1"></i> Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditExerciseSchedule;